import React, {ChangeEvent} from 'react';
import {curry} from 'ramda';
import {getContentAnchorEl} from '@pluto/utils';
import {
    Select,
    MenuItem,
    Checkbox,
    InputLabel,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';

import humanizeRange from '@pluto/humanize-moment-range';
import {
    QCSpecificFilters,
    QCSpecificFiltersType,
} from '../../QCWorkflow/QCFilters/QCFiltersType';
import './multi-select.scss';

export interface ReactSelectOpt {
    value: string;
    label: string;
}

export type OnChangeMultiSelectType = (e: ChangeEvent<{}>) => void;
export type PlutoSelectChange = (options: ReactSelectOpt[]) => OnChangeMultiSelectType;

interface MultiSelectProps {
    onChange: PlutoSelectChange;
    value: string[];
    options: ReactSelectOpt[];
    placeholder: string;
    isLoading: boolean;
    filters: QCSpecificFilters;
    filterKey: QCSpecificFiltersType;
}

export const isChecked = (filterValues: string[] | string, option: ReactSelectOpt): boolean => {
    return filterValues.includes(option.value);
};

export const getOptsFromQuery = curry((query, key): string[] => {
    const queryValues = query.getAll(key);

    if (!queryValues)
        return [];

    return queryValues;
});

export const getValue = (item: ReactSelectOpt) => item.value;

export const getNewValues = (newValues: string[], options: ReactSelectOpt[]): string[] => {
    if (!newValues || newValues.includes('none'))
        return [];

    if (newValues.includes('all'))
        return options.map(getValue);

    return newValues;
};

export const getMenuItemValue = (filterValues: string[] | string, options: ReactSelectOpt[]) => {
    return filterValues.length === options.length ? 'none' : 'all';
};

export const getMenuItemLabel = (filterValues: string[] | string, options: ReactSelectOpt[]) => {
    return filterValues.length === options.length ? 'Deselect all' : 'Select all';
};

export const getMenuItemOption = curry((isChecked, filterValues, option) => (
    <MenuItem
        key={option.value}
        value={option.value}
    >
        <Checkbox
            checked={isChecked(filterValues, option)}
        />
        {option.label}
    </MenuItem>
));

export const filterSelectedOption = curry((selectedValue: string[], option: ReactSelectOpt) => {
    return selectedValue.includes(option.value);
});

export const mapSelectedOption = (option: ReactSelectOpt) => {
    return `${option.label}, `;
};

export const getSelectedOptions = (options: ReactSelectOpt[], selectedValue: string[]) => {
    return options
        .filter(filterSelectedOption(selectedValue))
        .map(mapSelectedOption);
};

export const renderSelectValue = curry((options: ReactSelectOpt[], selectValue: string[]) => (
    getSelectedOptions(options, selectValue)
));

export const handleSelect = curry((filterHandlers, options: ReactSelectOpt[], event: ChangeEvent<{value: string[]}>) => {
    const {
        setFilters,
        setPage,
        setQuery,
        history,
        setQCJobs,
        rowsPerPage,
        filters,
        title,
        sortOrder,
        sortBy,
        route,
        dates,
        key,
    } = filterHandlers;
    const [dateStart, dateEnd] = humanizeRange(dates);
    const query = {
        ...filters,
        [key]: getNewValues(event.target.value, options),
        offset: 0,
        title,
        limit: rowsPerPage,
        sortOrder,
        sortBy,
        dateStart,
        dateEnd,
    };

    setPage(0);
    setQuery(history, route, query);
    setFilters(query);
    setQCJobs(query);
});

export const handleFind = curry((id: string, option: ReactSelectOpt) => option.value === id);

export const formatValues = curry((options: ReactSelectOpt[], id: string) => {
    const onFind = handleFind(id);
    return options.find(onFind);
});

export const getFilterValues = (filters: QCSpecificFilters, filterKey: QCSpecificFiltersType) => {
    return filters[filterKey] || [];
};

export default function MultiSelect(props: MultiSelectProps) {
    const {
        filters,
        filterKey,
        onChange,
        value,
        options,
        placeholder,
        isLoading,
    } = props;

    if (isLoading) {
        return (
            <CircularProgress />
        );
    }
    const filterValues = getFilterValues(filters, filterKey);
    const firstMenuItemValue = getMenuItemValue(filterValues, options);
    return (
        <>
            <InputLabel id={placeholder}>
                {placeholder}
            </InputLabel>
            <Select
                className="select-overwrite"
                labelId={placeholder}
                multiple
                value={value}
                onChange={onChange(options) as any}
                renderValue={renderSelectValue(options)}
                MenuProps={{
                    getContentAnchorEl
                }}
            >
                <MenuItem value={firstMenuItemValue}>
                    {getMenuItemLabel(filterValues, options)}
                </MenuItem>
                {options.map(getMenuItemOption(isChecked, filterValues))}
            </Select>
        </>
    );
}
